import React, { createContext, useContext, useState } from "react";
import { fetchBestTableAndMenu, createReservation, isDateOpened } from './query';
import { generateSlides, isAllowedTime, allowMenuChoose } from './utils';
import { useMatomo } from '@datapunt/matomo-tracker-react'

const ReservationContext = createContext();

export const ReservationProvider = ({ children }) => {
    const [reservationState, setReservationState] = useState({
        activeStep: 0,
        selectedDateTime: false,
        selectedDate: false,
        selectedTime: false,
        adults: 1,
        children: 0,
        needsBabyChair: false,
        slides: [],
        bestTable: "",
        name: '',
        contact: '',
        consent: true,
        additionalInfo: "",
        openErrorDialog: false,
        errorDialogMessage: "",
        source: "direct",
        restdays: {},
        isCritical: false,
        isSemiCritical: false
    });
    const { trackEvent } = useMatomo()

    const handleNext = async () => {
        const { activeStep, selectedDateTime, adults, children, slides, name, contact, consent, bestTable, source } = reservationState;

        if (activeStep === 0) {
            if (!selectedDateTime||!isAllowedTime(selectedDateTime)) {
                setReservationState({
                    ...reservationState,
                    openErrorDialog: true,
                    errorDialogMessage: "Wähle bitte ein gültiges Datum und Uhrzeit aus.",
                });
                return;
            }
            try {
                const isOpenedResult = await isDateOpened(selectedDateTime);
                if (!isOpenedResult) {
                    setReservationState({
                        ...reservationState,
                        openErrorDialog: true,
                        errorDialogMessage: "Leider haben wir an diesem Tag geschlossen.",
                    });
                    return;
                }                
                const result = await fetchBestTableAndMenu(selectedDateTime, adults + children);
                if (result.error || !result.bestTable) {
                    setReservationState({
                        ...reservationState,
                        openErrorDialog: true,
                        errorDialogMessage: result.error
                            ? "Ein Fehler ist aufgetreten. Bitte versuche es erneut."
                            : "Leider wurde für dieses Datum und diese Personenanzahl kein freier Tisch gefunden.",
                    });
                    return;
                }
                const generatedSlides = generateSlides(result.menu);
                setReservationState({
                    ...reservationState,
                    bestTable: result.bestTable,
                    slides: generatedSlides,
                    activeStep: activeStep + 1 + ((Object.entries(generatedSlides).length&&allowMenuChoose(selectedDateTime))?0:1),
                });
            } catch (error) {
                setReservationState({
                    ...reservationState,
                    openErrorDialog: true,
                    errorDialogMessage: "Interner Fehler aufgetreten. Bitte telefonisch reservieren.",
                });
            }
        } else if (activeStep === 1) {
            const totalAssignedAdults = slides.reduce((sum, slide) => sum + (slide.adults || 0), 0);
            const totalAssignedChildren = slides.reduce((sum, slide) => sum + (slide.children || 0), 0);

            if (totalAssignedAdults !== adults || totalAssignedChildren !== children) {
                setReservationState({
                    ...reservationState,
                    openErrorDialog: true,
                    errorDialogMessage: "Bitte verwende die + und - Buttons, um Personen hinzuzufügen oder zu entfernen.",
                });
                return;
            }
            setReservationState({
                ...reservationState,
                activeStep: activeStep + 1,
            });
        } else if (activeStep === 2) {
            if (!name || !contact || !consent) {
                setReservationState({
                    ...reservationState,
                    openErrorDialog: true,
                    errorDialogMessage: name && contact
                        ? "Bitte akzeptiere die Speicherung der Daten."
                        : ( name ?
                            "Bitte gib eine E-Mail Adresse oder Telefonnummer an."
                            : "Bitte gib einen Namen an."),
                });
                return;
            }
            try {
                await createReservation(
                    name,
                    adults,
                    children,
                    reservationState.needsBabyChair,
                    bestTable.documentId,
                    selectedDateTime,
                    contact,
                    reservationState.additionalInfo,
                    source,
                    slides.map((slide) => ({
                        adults: slide.adults,
                        children: slide.children,
                        menu: slide.id,
                    }))
                );
                trackEvent({ category: 'conversion', name: source, action: 'click-event', value: (adults+children) })
                setReservationState({
                    ...reservationState,
                    activeStep: activeStep + 1,
                });
            } catch (error) {
                setReservationState({
                    ...reservationState,
                    openErrorDialog: true,
                    errorDialogMessage: "Interner Fehler aufgetreten. Bitte telefonisch reservieren.",
                });
            }
        }
    };

    const handleBack = () => {
        setReservationState((prevState) => ({
            ...prevState,
            activeStep: prevState.activeStep - 1 - ((Object.entries(reservationState.slides).length&&allowMenuChoose(prevState.selectedDateTime))?0:1),
        }));
    };

    
    const handleReset = () => {
        setReservationState((prevState) => ({
            activeStep: 0,
        }));
    };

    const handleCloseDialog = () => {
        setReservationState((prevState) => ({
            ...prevState,
            openErrorDialog: false,
        }));
    };

    return (
        <ReservationContext.Provider value={{ reservationState, setReservationState, handleNext, handleBack, handleReset, handleCloseDialog }}>
            {children}
        </ReservationContext.Provider>
    );
};

export const useReservation = () => useContext(ReservationContext);
